import moment, { Moment } from '@e3dc-react/shell/Libs/moment'
import {
  asocToBatCapa as asocToBatteryCapacity,
  batteryCapacityToAsoc,
  FIFTEEN_MINUTES_MS,
  getBreakthroughDataFromStore,
  getForecastPowerDataFromStore,
  getHistoryDataFromStore,
  getPowermeterHistoryDataFromStore,
  getRoundedNumber,
  getWallboxHistoryDataFromStore,
} from './utils/helperFunctions'

import {
  fillTimeValues,
  ForecastChartData,
  getForecastChartDataForView,
  getHistoryChartDataForView,
  getSums,
  HistoryChartData,
  getMatchingTimestampItemFromArray,
} from './utils/chartFunctions'
import { ChartView, RechartData } from '../chart-kit/shared/utils/chartModel'
import { WallboxHistory } from '../../Types/responses/wallboxes/{sn}'
import { PowermeterHistory } from '../../Types/responses/powermeter-values/{sn}'
import { Breakthrough, ForecastData, SystemInfo, TemperatureData } from './models/systemDataStoreModel'
import { getStore } from './state/store'
import { getFetchRanges, TimeRange } from './utils/datetime'
import { binarySearchObject } from './utils/binarySearch'
import { i18n, t } from 'i18next'
import { ThreeSixtySavingsTotalConsumption } from '../dynamic-power-prices/hooks/use-three-sixty-savings-consumption'

export const JS_VERSION = 1
export const DATA_RELOAD_INTERVAL = 60 * 1000 // 1 minute

/**
 * The CPE-2043 had 2 hours of time difference for the forecast power in portalV2
 * There were extra 15 minutes are probably needed to calculate the difference
 * Needs to be checked if this is still needed
 */
export const HISTORY_FIELDS = Object.freeze([
  'time',
  'BatPowerIn',
  'BatPowerOut',
  'NetIn',
  'NetOut',
  'Consumption',
  'ASOC',
  'DC_PowerL1',
  'DC_PowerL2',
  'DC_PowerL3',
])
export const WALLBOX_FIELDS = Object.freeze(['time', 'energyAll', 'energySolar', 'energyDischarged'])
export const POWERMETER_FIELDS = Object.freeze(['time', 'NetIn', 'NetOut'])

export const updateChartData = ({
  serialNo,
  localFrom,
  localTo,
  view,
  threeSixtySavingsTotalConsumption,
  isDynamicPowerPriceEnabled,
}: {
  serialNo: string
  localFrom: Moment
  localTo: Moment
  view: ChartView
  t?: i18n
  threeSixtySavingsTotalConsumption: ThreeSixtySavingsTotalConsumption
  isDynamicPowerPriceEnabled: boolean /* this is a temporary condition added with AF-3237. The plan is to remove it after SW1 and SW2 align. */
}): RechartData => {
  const store = getStore()

  if (!store) {
    return { loadingState: { state: 0 } }
  }
  const systemInfo = store.getState().systemInfo[serialNo]
  const systemInfoData = systemInfo?.data

  if (!systemInfoData) {
    return {
      loadingState: systemInfo?.state === 'FETCHED' ? { state: 1 } : { state: 0 },
      error: systemInfo?.state === 'FETCHED' ? t('systemDataHandler.noSystemInfoData') : undefined,
    }
  }
  const monthsInRange = getFetchRanges(localFrom, localTo)

  const loadedMonths = monthsInRange.filter((month: TimeRange) => !!store.getState().historyData[serialNo]?.[month.from]?.data).length

  if (monthsInRange.length !== loadedMonths) {
    const currentFetchingMonth = monthsInRange[loadedMonths]
    const currentMonthTs = currentFetchingMonth.from
    const monthAmountToFetch = monthsInRange.length

    return { loadingState: { state: 0, currentMonthTs, monthAmountToFetch, fetchedMonths: loadedMonths } }
  }

  const breakthroughAvailable = view === 'day' || view === 'week'
  const forecastAvailable = Date.now() < localTo.valueOf() && breakthroughAvailable
  const forecastPower = getForecastPowerDataFromStore(localFrom, localTo, serialNo)
  const extendedLocalFrom = localFrom.clone()
  const extendedLocalTo = localTo.clone()
  /*
   *Get the first entry from the first day of the following year (e.g 01.01.2024 00:00:00)
   */
  extendedLocalTo.add(2, 'hour')
  extendedLocalFrom.subtract(2, 'hour')

  const historyData = getHistoryDataFromStore(extendedLocalFrom, extendedLocalTo, serialNo, view)
  const wallboxChartData = getWallboxHistoryDataFromStore(extendedLocalFrom, extendedLocalTo, serialNo)
  const powermeterChartData = getPowermeterHistoryDataFromStore(extendedLocalFrom, extendedLocalTo, serialNo)
  const breakthroughData = breakthroughAvailable ? getBreakthroughDataFromStore(extendedLocalFrom, extendedLocalTo, serialNo) : []
  const historyChartData = getHistoryChartDataForView(
    historyData,
    wallboxChartData,
    powermeterChartData,
    forecastPower,
    breakthroughData,
    systemInfoData,
    view,
    isDynamicPowerPriceEnabled
  )

  const forecastData = calculateForecast(localFrom, localTo, serialNo, wallboxChartData, powermeterChartData, systemInfoData, view)
  const forecastChartData = getForecastChartDataForView(
    forecastData,
    forecastPower,
    wallboxChartData,
    powermeterChartData,
    breakthroughData,
    systemInfoData,
    view
  )

  //make first forecast ts = last history ts if there is more than one history data
  // historyChartData.length - 2, the 2 was one, but some changes were introduced, so we have 3 historyChartData items with the same ts, and the last one has no real values
  const lastHistory = historyChartData.length > 1 ? historyChartData?.[historyChartData.length - 2] : undefined

  const firstForecast: Partial<ForecastData> = {
    ForecastPower: lastHistory?.ForecastPower,
    ts: lastHistory?.ts,
  }

  if (lastHistory) {
    Object.keys(lastHistory).forEach(key => {
      if (key !== 'ts' && key !== 'ForecastPower') {
        firstForecast[`f-${key}`] = lastHistory[key]
      }
    })
  }

  const chartDataArray: (HistoryChartData | ForecastChartData | { ts: number })[] = []
  const historyChartDataForSums: HistoryChartData[] = []

  if (historyData.length || forecastData.length) {
    const filledTimeValues = fillTimeValues({ from: localFrom, to: localTo, view })
    filledTimeValues.forEach(entry => {
      const entryTs = entry.ts
      let isEmptyPoint = true
      const isFirstForecast = forecastAvailable && firstForecast.ts === entryTs

      const historyDataItem =
        binarySearchObject(historyChartData ?? [], entryTs, 'ts') || getMatchingTimestampItemFromArray(historyChartData, entryTs, view)

      if (historyDataItem && !isFirstForecast) {
        chartDataArray.push(historyDataItem)
        historyChartDataForSums.push(historyDataItem)
        isEmptyPoint = false
      }

      if (forecastAvailable && isFirstForecast) {
        chartDataArray.push({ ...historyDataItem, ...firstForecast, forecast: true } as ForecastChartData)
        historyChartDataForSums.push({ ...historyDataItem, ...firstForecast, forecast: true } as HistoryChartData)
        isEmptyPoint = false
      } else {
        const forecastDataItem = binarySearchObject(forecastChartData ?? [], entryTs, 'ts')
        if (forecastDataItem) {
          chartDataArray.push({ ...forecastDataItem, forecast: true })
          isEmptyPoint = false
        }

        if (isEmptyPoint) {
          chartDataArray.push(entry)
        }
      }
    })
  }

  /** check if theres any breakthrough-data available for the selected time-frame */
  const hasWeatherBreakthrough = !!breakthroughData.find(breakthrough => {
    const localFromInLocalTime = moment(localFrom)
    const breakthroughDate = moment(breakthrough.deratePowerAt)

    if (breakthroughDate.isSame(localFromInLocalTime, 'day')) return breakthrough
    return null
  })

  const chartData = {
    data: chartDataArray,
    sums: getSums(historyChartDataForSums, forecastChartData, forecastPower, view === 'day' ? 4 : 1, threeSixtySavingsTotalConsumption),
    loadingState: { state: 1 },
    hasWeatherBreakthrough,
  } as RechartData

  return chartData
}

export const linearInterpolation = <T extends { time: number; [key: string]: number }>(
  values: ReadonlyArray<T>,
  valueKey: keyof T,
  factory: (time: number, value: number) => T
): T[] => {
  const interpolated: T[] = []
  for (let i = 1; i < values.length; i++) {
    const cur = values[i - 1]
    const next = values[i]
    const currentPower = cur[valueKey] ?? 0
    const nextPower = next[valueKey] ?? 0
    interpolated.push(cur)
    interpolated.push(factory(cur.time + (1 * (next.time - cur.time)) / 4, currentPower + (1 * (nextPower - currentPower)) / 4))
    interpolated.push(factory(cur.time + (2 * (next.time - cur.time)) / 4, currentPower + (2 * (nextPower - currentPower)) / 4))
    interpolated.push(factory(cur.time + (3 * (next.time - cur.time)) / 4, currentPower + (3 * (nextPower - currentPower)) / 4))
  }

  return interpolated
}

export const interpolatedTemperature = (values: ReadonlyArray<TemperatureData>): TemperatureData[] => {
  return linearInterpolation(values as any, 'value', (t, p) => ({ time: t, value: p })) as any as TemperatureData[] //eslint-disable-line
}

const addWallboxDataToForecastItem = (
  wallboxChartData: Record<number, WallboxHistory[]>,
  timestamp: number,
  timestampNext: number,
  forecastItem: ForecastData
): void => {
  let energySolarSum = 0
  let energyAllSum = 0
  let energyDischargedSum = 0

  Object.entries(wallboxChartData).forEach(([id, data]) => {
    const currentWallbox = binarySearchObject(data, timestamp, 'time')
    const nextWallbox = binarySearchObject(data, timestampNext, 'time')

    if (currentWallbox && nextWallbox) {
      const energyAll = getRoundedNumber(Math.max(nextWallbox.energyAll - currentWallbox.energyAll, 0))
      const energySolar = getRoundedNumber(Math.max(nextWallbox.energySolar - currentWallbox.energySolar, 0))
      const energyDischarged = getRoundedNumber(Math.max(nextWallbox.energyDischarged - currentWallbox.energyDischarged, 0))
      forecastItem[`wb${id}/Wallbox_energyAll`] = energyAll
      forecastItem[`wb${id}/Wallbox_energySolar`] = energySolar
      forecastItem[`wb${id}/Wallbox_energyDischarged`] = energyDischarged
      forecastItem[`wb${id}/Wallbox_energyNetOut`] = energyAll - energySolar
      energyAllSum += energyAll
      energySolarSum += energySolar
      energyDischargedSum += energyDischarged
    }
  })

  forecastItem['Wallbox_energySolar'] = energySolarSum
  forecastItem['Wallbox_energyAll'] = energyAllSum
  forecastItem['Wallbox_energyDischarged'] = energyDischargedSum
}

const addPowerMeterDataToForecastItem = (
  powermeterChartData: Record<number, PowermeterHistory[]>,
  systemInfo: SystemInfo,
  timestamp: number,
  timestampNext: number,
  forecastItem: ForecastData
): void => {
  let consumptionSum = 0
  let bypassNoChargeSum = 0

  Object.entries(powermeterChartData).forEach(([id, data]) => {
    const powermeterInfo = systemInfo.powermeters.powermeters.find(pm => pm.id.toString() === id)
    const currentPowermeterType = powermeterInfo?.type

    const currentPowermeter = binarySearchObject(data, timestamp, 'time')
    const nextPowermeter = binarySearchObject(data, timestampNext, 'time')

    if (currentPowermeter && nextPowermeter) {
      const deltaNetIn = getRoundedNumber(Math.max((nextPowermeter.NetIn - currentPowermeter.NetIn) * 4, 0))
      const deltaNetOut = getRoundedNumber(Math.max((nextPowermeter.NetOut - currentPowermeter.NetOut) * 4, 0))

      if (currentPowermeterType?.includes('consumption')) {
        forecastItem[`pm${id}/Powermeter_consumption`] = deltaNetOut
        consumptionSum += deltaNetOut
      }

      if (currentPowermeterType?.includes('bypassNoCharge')) {
        forecastItem[`pm${id}/Powermeter_NetIn_bypass_noCharge`] = deltaNetIn
        bypassNoChargeSum += deltaNetIn
      }
    }
  })

  forecastItem['Powermeter_consumption'] = getRoundedNumber(consumptionSum)
  forecastItem['ConsumptionHouse'] = getRoundedNumber(
    Math.max((forecastItem['ConsumptionTotal'] as number) - forecastItem['Powermeter_consumption'] - (forecastItem['Wallbox_energyAll'] as number), 0)
  )
  forecastItem['Powermeter_NetIn_bypass_noCharge'] = getRoundedNumber(bypassNoChargeSum)
  forecastItem['DC_PowerTotal'] = getRoundedNumber((forecastItem['Powermeter_production'] as number) + (forecastItem['DC_PowerSolar'] as number))
}

export const calculateForecast = (
  from: Moment,
  to: Moment,
  serialNo: string,
  wallboxChartData: Record<number, WallboxHistory[]>,
  powermeterChartData: Record<number, PowermeterHistory[]>,
  systemInfo: SystemInfo | null,
  view?: ChartView
): ForecastData[] => {
  if (!view || view === 'month' || view === 'year') return []
  const store = getStore()
  if (!store) return []

  if (!systemInfo) return []
  // Todo: Wieso wird Breakthrough nur nach 00:00 gerechnet? Was wenn der letzte halt am Vortag war?
  // zB BT 23:30
  const month = moment(from).startOf('month')
  const prevMonth = moment(month).subtract(1, 'month')
  const monthTs = month.valueOf()
  const prevMonthTs = prevMonth.valueOf()
  const historyDataCurMonth = store.getState().historyData[serialNo]?.[monthTs]?.data ?? []
  const historyDataPrevMonth = store.getState().historyData[serialNo]?.[prevMonthTs]?.data ?? []

  const historyData = [...historyDataPrevMonth, ...historyDataCurMonth]

  if (!historyData || !historyData.length) return []

  const lastHistoryEntry = historyData[historyData.length - 1]
  const lastHistoryTimestamp = lastHistoryEntry.time

  const forecastPowerRaw = store.getState().forecastPower[serialNo]?.[monthTs] ?? []
  /** filter out null values */
  const validForecastPower = forecastPowerRaw.filter(item => item?.time !== undefined)
  const breakthrough: Breakthrough[] = store.getState().breakthrough[serialNo]?.[monthTs] ?? []
  if (!validForecastPower.length) return []

  //Get last breakthrough timed before NOW
  const lastBreakthroughOfDay = breakthrough?.filter(bt => bt.deratePowerAt <= lastHistoryTimestamp).slice(-1)
  const batteryPowerLimitQuarter = systemInfo ? systemInfo.storage.info.batteryPower / 4 : 0
  const lastAsoc = historyData[historyData.length - 1].ASOC

  let charge = !!lastBreakthroughOfDay
  let currentBatteryLevel = asocToBatteryCapacity(lastAsoc, systemInfo?.storage.info.batteryCapacity)
  let currentForecastBatteryInCounterStatus = lastHistoryEntry.BatPowerIn
  let currentForecastBatteryOutCounterStatus = lastHistoryEntry.BatPowerOut
  let currentForecastNetInCounterStatus = lastHistoryEntry.NetIn
  let currentForecastNetOutCounterStatus = lastHistoryEntry.NetOut
  let currentForecastConsumptionCounterStatus = lastHistoryEntry.Consumption
  let asoc = lastAsoc

  /**
   * We subtract 15 Minutes to have the correct amount of timestamps for the sum calculation.
   * TODO: check if that is still needed. Was done for the CPE-2043
   */
  const forecastTimestamps = calculateForecastTimestamps(lastHistoryTimestamp).filter(ts => ts <= to.valueOf() - FIFTEEN_MINUTES_MS)

  /**
   * Since we handle accumulated values and then calculate the difference between them
   * we need to add the last history object, so the differences can be calculated
   */
  const lastHistoryForecastObject: ForecastData = {
    asoc: lastHistoryEntry.ASOC,
    batteryCapacity: currentBatteryLevel,
    batteryPowerIn: lastHistoryEntry.BatPowerIn,
    batteryPowerIn_delta: 0,
    batteryPowerOut: lastHistoryEntry.BatPowerOut,
    batteryPowerOut_delta: 0,
    consumption: lastHistoryEntry.Consumption,
    consumption_delta: 0,
    forecastPower: binarySearchObject(validForecastPower, lastHistoryEntry.time, 'time')?.power ?? 0,
    netIn: lastHistoryEntry.NetIn,
    netIn_delta: 0,
    netOut: lastHistoryEntry.NetOut,
    netOut_delta: 0,
    time: lastHistoryEntry.time,
  }

  addWallboxDataToForecastItem(wallboxChartData, lastHistoryEntry.time, lastHistoryEntry.time + FIFTEEN_MINUTES_MS, lastHistoryForecastObject)
  addPowerMeterDataToForecastItem(
    powermeterChartData,
    systemInfo,
    lastHistoryEntry.time,
    lastHistoryEntry.time + FIFTEEN_MINUTES_MS,
    lastHistoryForecastObject
  )

  const forecastArray = forecastTimestamps.map(timestamp => {
    const isBreakthrough = binarySearchObject(breakthrough, timestamp, 'deratePowerAt')
    // If day has changed since last breakthrough, stop charging
    if (moment(timestamp).isSame(moment(timestamp).startOf('day'))) {
      charge = false
    }

    // If current timestamp IS a breakthrough start charging
    if (isBreakthrough) {
      charge = true
    }

    // Compare with same day/time a week ago
    const referenceTimestamp = moment(timestamp).subtract(1, 'week').valueOf()
    const referenceTimestampNext = referenceTimestamp + FIFTEEN_MINUTES_MS
    const referenceConsumption = binarySearchObject(historyData, referenceTimestamp, 'time')?.Consumption
    const referenceConsumptionNext = binarySearchObject(historyData, referenceTimestampNext, 'time')?.Consumption
    const consumptionTotal = referenceConsumption && referenceConsumptionNext ? referenceConsumptionNext - referenceConsumption : undefined
    /** TODO: revert the changes of CPE-2043 when needed. This was added to have the same forecast power values as in portalV2  */
    const predictedPower = (binarySearchObject(validForecastPower, timestamp, 'time')?.power ?? 0) / 4 //power is per hour, we need per 15min

    //eslint-disable-next-line
    if (consumptionTotal != undefined && predictedPower != undefined) {
      if (predictedPower > consumptionTotal) {
        let powerIn = predictedPower - consumptionTotal
        let powerRest = 0

        if (!charge) {
          // we dont charge before breakthrough! everything goes into netOut
          powerRest = powerIn
          powerIn = 0
        }

        // limit to battery power
        if (powerIn > batteryPowerLimitQuarter) {
          powerRest += powerIn - batteryPowerLimitQuarter
          powerIn = batteryPowerLimitQuarter
        }

        if (currentBatteryLevel + powerIn > systemInfo.storage.info.batteryCapacity) {
          const overflow = currentBatteryLevel + powerIn - systemInfo.storage.info.batteryCapacity
          powerRest += overflow
          powerIn -= overflow
        }

        currentForecastBatteryInCounterStatus += powerIn
        currentForecastBatteryOutCounterStatus = 0
        currentBatteryLevel += powerIn
        asoc = batteryCapacityToAsoc(currentBatteryLevel, systemInfo.storage.info.batteryCapacity)
        currentForecastNetInCounterStatus += powerRest
        currentForecastConsumptionCounterStatus += consumptionTotal
        currentForecastNetOutCounterStatus = 0
      } else if (predictedPower < consumptionTotal) {
        let powerOut = consumptionTotal - predictedPower
        let powerRest = 0

        if (powerOut > batteryPowerLimitQuarter) {
          powerRest += powerOut - batteryPowerLimitQuarter
          powerOut = batteryPowerLimitQuarter
        }

        if (currentBatteryLevel - powerOut < 0) {
          const overflow = powerOut - currentBatteryLevel
          powerRest += overflow
          powerOut -= overflow
        }

        currentForecastBatteryInCounterStatus = 0
        currentForecastBatteryOutCounterStatus += Math.max(powerOut, 0)
        currentBatteryLevel -= powerOut
        asoc = batteryCapacityToAsoc(currentBatteryLevel, systemInfo.storage.info.batteryCapacity)
        currentForecastNetOutCounterStatus += Math.max(powerRest, 0)
        currentForecastConsumptionCounterStatus += consumptionTotal
        currentForecastNetInCounterStatus = 0
      } else {
        currentForecastBatteryInCounterStatus = 0
        currentForecastBatteryOutCounterStatus = 0
        // batCapa and asoc stays unchanged
        currentForecastNetInCounterStatus = 0
        currentForecastNetOutCounterStatus = 0
      }
    }
    const resultObject: ForecastData = {
      /** add fifteen minutes, because the first forecast is taken from the lastHistory */
      time: timestamp + FIFTEEN_MINUTES_MS,
      batteryPowerIn_delta: 0,
      batteryPowerIn: currentForecastBatteryInCounterStatus,
      batteryPowerOut: currentForecastBatteryOutCounterStatus,
      batteryPowerOut_delta: 0,
      batteryCapacity: Math.max(currentBatteryLevel, 0),
      asoc,
      netIn_delta: 0,
      netIn: currentForecastNetInCounterStatus,
      netOut: currentForecastNetOutCounterStatus,
      netOut_delta: 0,
      consumption_delta: 0,
      consumption: currentForecastConsumptionCounterStatus,
      forecastPower: Math.max(predictedPower * 4, 0),
      WeatherBreakthrough: !!isBreakthrough,
    }

    if (wallboxChartData) {
      addWallboxDataToForecastItem(wallboxChartData, referenceTimestamp, referenceTimestampNext, resultObject)
    }

    if (powermeterChartData) {
      addPowerMeterDataToForecastItem(powermeterChartData, systemInfo, referenceTimestamp, referenceTimestampNext, resultObject)
    }

    return resultObject
  })

  return [lastHistoryForecastObject, ...forecastArray]
}

const calculateForecastTimestamps = (lastHistoryTimestamp: number): number[] => {
  const forecastTimestamps = []
  const now = moment(lastHistoryTimestamp).valueOf()
  const theDayAfterTomorrow = moment(lastHistoryTimestamp).add(2, 'days').valueOf()

  for (let timestamp = now; timestamp < theDayAfterTomorrow; timestamp += FIFTEEN_MINUTES_MS) {
    forecastTimestamps.push(timestamp)
  }

  return forecastTimestamps
}

export const isStoreLoaded = (): boolean => {
  return !!getStore()
}
